// src/components/Transaction/BuyDialog.js - Updated for quantity-based transactions

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { X, MapPin, PackageCheck, Plus, Minus, ShoppingCart, Send, ChevronDown } from 'lucide-react';
import { TransactionService } from '../../services/TransactionService';
import { AuthProvider, useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';


const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
`;

const DialogContent = styled.div`
  background: ${props => props.theme?.colors?.background || '#111'};
  border-radius: ${props => props.theme?.styles?.borderRadius || '12px'};
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#800000'}30`};
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: ${props => props.theme?.colors?.text || 'white'};
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s;
  
  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
`;

const Title = styled.h2`
  font-family: ${props => props.theme?.fonts?.heading || 'inherit'};
  color: ${props => props.theme?.colors?.text || 'white'};
  margin-top: 0;
  margin-bottom: 1.5rem;
`;

const ItemDetails = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  img {
    width: 100px;
    height: 100px;
    object-fit: cover;
    border-radius: 8px;
  }
  
  .details {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1;
    
    h3 {
      margin: 0 0 0.5rem 0;
      font-family: ${props => props.theme?.fonts?.heading || 'inherit'};
    }
    
    .price {
      font-size: 1.2rem;
      font-weight: bold;
      color: ${props => props.theme?.colors?.accent || '#800000'};
    }
    
    .seller {
      font-size: 0.9rem;
      opacity: 0.8;
    }
    
    .availability {
      font-size: 0.9rem;
      color: ${props => props.availableQuantity > 0 ? '#4CAF50' : '#F44336'};
      font-weight: 500;
    }
  }
`;

const QuantitySection = styled.div`
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  .quantity-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    
    .label {
      font-weight: bold;
      color: ${props => props.theme?.colors?.text || 'white'};
    }
    
    .available {
      font-size: 0.9rem;
      opacity: 0.8;
      color: ${props => props.theme?.colors?.accent || '#800000'};
    }
  }
  
  .quantity-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    
    .quantity-btn {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
      background: transparent;
      color: ${props => props.theme?.colors?.accent || '#800000'};
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s;
      
      &:hover:not(:disabled) {
        background: ${props => props.theme?.colors?.accent || '#800000'};
        color: white;
      }
      
      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
    }
    
    .quantity-display {
      font-size: 1.5rem;
      font-weight: bold;
      color: ${props => props.theme?.colors?.text || 'white'};
      min-width: 60px;
      text-align: center;
    }
  }
  
  .quantity-input {
    margin-top: 1rem;
    
    input {
      width: 100%;
      padding: 0.75rem;
      background: rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      color: white;
      text-align: center;
      font-size: 1.1rem;
      
      &:focus {
        outline: none;
        border-color: ${props => props.theme?.colors?.accent || '#800000'};
      }
    }
    
    .input-note {
      font-size: 0.8rem;
      opacity: 0.7;
      text-align: center;
      margin-top: 0.5rem;
    }
  }
`;

const PriceBreakdown = styled.div`
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgba(0, 188, 212, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(0, 188, 212, 0.3);
  
  .breakdown-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    
    &.total {
      font-weight: bold;
      font-size: 1.1rem;
      color: #00BCD4;
      border-top: 1px solid rgba(0, 188, 212, 0.3);
      padding-top: 0.5rem;
      margin-top: 0.5rem;
    }
  }
`;

const MeetupOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  .title {
    font-weight: bold;
    margin-bottom: 0.5rem;
  }
`;

const MeetupOption = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  background: ${props => props.selected ? 
    `${props.theme?.colors?.accent}20` || 'rgba(128, 0, 0, 0.2)' : 
    `${props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'}`
  };
  border: 1px solid ${props => props.selected ? 
    props.theme?.colors?.accent || '#800000' : 
    'transparent'
  };
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => `${props.theme?.colors?.accent}10` || 'rgba(128, 0, 0, 0.1)'};
  }
  
  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${props => props.selected ? 
      props.theme?.colors?.accent || '#800000' : 
      'rgba(255, 255, 255, 0.1)'
    };
    color: ${props => props.selected ? 'white' : props.theme?.colors?.text || 'white'};
  }
  
  .info {
    flex: 1;
    
    .title {
      font-weight: bold;
      margin-bottom: 0.25rem;
    }
    
    .description {
      font-size: 0.9rem;
      opacity: 0.8;
    }
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 1rem;
  border-radius: 8px;
  background: ${props => props.theme?.colors?.accent || '#800000'};
  color: white;
  border: none;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme?.colors?.primary || '#600000'};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  
  button {
    flex: 1;
    padding: 0.75rem;
    border: none;
    border-radius: 8px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    
    @media (min-width: 768px) {
      padding: 1rem;
      font-size: 1rem;
    }
    
    &.add-to-cart {
      background: ${props => props.theme?.colors?.accent || '#800000'};
      color: white;
      
      &:hover:not(:disabled) {
        background: ${props => props.theme?.colors?.primary || '#4A0404'};
        transform: translateY(-2px);
      }
    }
    
    &.order-now {
      background: transparent;
      border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
      color: ${props => props.theme?.colors?.accent || '#800000'};
      
      &:hover:not(:disabled) {
        background: ${props => props.theme?.colors?.accent || '#800000'};
        color: white;
      }
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const RestaurantSection = styled.div`
  margin: 1rem 0;
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#800000'}30`};
  border-radius: 10px;
  overflow: hidden;
`;

const RestaurantToggle = styled.button`
  width: 100%;
  background: ${props => `${props.theme?.colors?.accent || '#800000'}10`};
  border: none;
  padding: 0.75rem 1rem;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.2s;

  &:hover { background: ${props => `${props.theme?.colors?.accent || '#800000'}18`}; }

  svg { transition: transform 0.25s ease; transform: ${props => props.$open ? 'rotate(180deg)' : 'rotate(0deg)'}; }
`;

const RestaurantPanel = styled.div`
  padding: ${props => props.$open ? '1rem' : '0'};
  max-height: ${props => props.$open ? '600px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.2s;
`;

const OptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.4rem;
  cursor: pointer;
  border-radius: 6px;
  font-size: 0.9rem;
  color: ${props => props.theme?.colors?.text || '#fff'};
  transition: background 0.15s;
  &:hover { background: ${props => `${props.theme?.colors?.accent || '#800000'}12`}; }
  input { accent-color: ${props => props.theme?.colors?.accent || '#800000'}; }
`;

const SideItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.4rem;
  cursor: pointer;
  border-radius: 6px;
  font-size: 0.85rem;
  color: ${props => props.theme?.colors?.text || '#fff'};
  transition: background 0.15s;
  &:hover { background: ${props => `${props.theme?.colors?.accent || '#800000'}12`}; }
  input { accent-color: ${props => props.theme?.colors?.accent || '#800000'}; }
  .side-price { margin-left: auto; opacity: 0.7; font-size: 0.8rem; }
`;

const SectionLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  opacity: 0.8;
  margin: 0.75rem 0 0.35rem;
`;

const InstructionsField = styled.textarea`
  width: 100%;
  background: rgba(255,255,255,0.05);
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#800000'}30`};
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
  color: ${props => props.theme?.colors?.text || '#fff'};
  font-size: 0.85rem;
  resize: none;
  height: 70px;
  box-sizing: border-box;
  margin-top: 0.35rem;
  &:focus { outline: none; border-color: ${props => props.theme?.colors?.accent || '#800000'}; }
`;

const BuyDialog = ({ item, sellerId, onClose, onTransactionCreated }) => {
  const [requestedQuantity, setRequestedQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Restaurant customization state
  const [shopStoreType, setShopStoreType] = useState(item.shopStoreType || null);
  const [shopItems, setShopItems] = useState([]);
  const [restaurantOpen, setRestaurantOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [selectedSides, setSelectedSides] = useState(new Set());
  const [specialInstructions, setSpecialInstructions] = useState('');

  // Structured attributes selection (for non-restaurant items)
  const [selectedAttributes, setSelectedAttributes] = useState(() => {
    const attrs = item.attributes || [];
    const init = {};
    attrs.forEach(a => { init[a.name] = ''; });
    return init;
  });

  // Fetch shop data for restaurant features
  useEffect(() => {
    if (!sellerId) return;
    getDoc(doc(db, 'shops', sellerId)).then(snap => {
      if (snap.exists()) {
        const data = snap.data();
        setShopStoreType(prev => prev || data.storeType || null);
        setShopItems((data.items || []).filter(i => i.id !== item.id && !i.hidden && i.visible !== false));
      }
    }).catch(() => {});
  }, [sellerId, item.id]);

  const isRestaurant = shopStoreType === 'restaurant';
  const validOptions = (item.customOptions || []).filter(o => o && o.trim());

  // Group side items by category (excluding main item)
  const sidesByCategory = shopItems.reduce((acc, si) => {
    const cat = si.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(si);
    return acc;
  }, {});
  
  const availableQuantity = parseInt(item.quantity) || 0;
  const unitPrice = parseFloat(item.price) || 0;
  const totalPrice = unitPrice * requestedQuantity;

  const handleQuantityChange = (delta) => {
    const newQuantity = Math.max(1, Math.min(availableQuantity, requestedQuantity + delta));
    setRequestedQuantity(newQuantity);
  };
  
  const handleQuantityInput = (e) => {
    const value = parseInt(e.target.value) || 1;
    setRequestedQuantity(Math.max(1, Math.min(availableQuantity, value)));
  };
  
  const buildCustomization = () => {
    // Non-restaurant attributes
    if (!isRestaurant) {
      const filled = Object.entries(selectedAttributes).filter(([, v]) => v);
      if (filled.length === 0) return null;
      return { selectedAttributes: Object.fromEntries(filled) };
    }
    // Restaurant customization
    const sides = shopItems.filter(si => selectedSides.has(si.id)).map(si => ({ id: si.id, name: si.name, price: si.price }));
    return {
      option: selectedOption || null,
      sides: sides.length > 0 ? sides : null,
      instructions: specialInstructions.trim() || null
    };
  };

  // ADD: Add to cart function
  const handleAddToCart = async () => {
    try {
      setLoading(true);
      setError(null);

      if (requestedQuantity > availableQuantity) {
        throw new Error('Requested quantity exceeds available stock');
      }

      if (requestedQuantity < 1) {
        throw new Error('Quantity must be at least 1');
      }

      const isService = item.hasOwnProperty('slots');

      const result = await TransactionService.addToCart(
        item.id,
        sellerId,
        unitPrice,
        requestedQuantity,
        'inperson',
        isService,
        buildCustomization()
      );

      if (result.transactionId) {
        onTransactionCreated(result.transactionId);
        onClose();
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // MODIFY: Order now creates cart and sends immediately
  const handleOrderNow = async () => {
    try {
      setLoading(true);
      setError(null);

      if (requestedQuantity > availableQuantity) {
        throw new Error('Requested quantity exceeds available stock');
      }

      if (requestedQuantity < 1) {
        throw new Error('Quantity must be at least 1');
      }

      const isService = item.hasOwnProperty('slots');

      if (!isService) {
        const existing = await TransactionService.addItemToActiveOrder(
          item.id, sellerId, unitPrice, requestedQuantity, false, !!item.isDigital, buildCustomization()
        );
        if (existing?.chatId) {
          onTransactionCreated(existing.chatId);
          onClose();
          return;
        }
      }

      const result = await TransactionService.initiateQuantityTransaction(
        item.id,
        sellerId,
        unitPrice,
        requestedQuantity,
        'inperson',
        isService,
        buildCustomization()
      );

      if (result.transactionId) {
        onTransactionCreated(result.transactionId);
        onClose();
      }
    } catch (error) {
      console.error('Error initiating transaction:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <DialogOverlay onClick={onClose}>
      <DialogContent 
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }} 
        theme={item.theme}
      >
        <CloseButton onClick={onClose} theme={item.theme}>
          <X size={20} />
        </CloseButton>
        
        <Title theme={item.theme}>Purchase Request</Title>
        
        <ItemDetails theme={item.theme} availableQuantity={availableQuantity}>
          {item.images && item.images[0] && (
            <img src={item.images[0]} alt={item.name} />
          )}
          <div className="details">
            <h3>{item.name}</h3>
            <div className="price">${unitPrice.toFixed(2)} each</div>
            <div className="seller">Seller: {item.shopName}</div>
            <div className="availability">
              {availableQuantity > 0 ? 
                `${availableQuantity} available` : 
                'Out of stock'
              }
            </div>
          </div>
        </ItemDetails>
        
        {availableQuantity > 0 && (
          <>
            <QuantitySection theme={item.theme}>
              <div className="quantity-header">
                <span className="label">Select Quantity</span>
                <span className="available">{availableQuantity} available</span>
              </div>
              
              <div className="quantity-controls">
                <button 
                  className="quantity-btn"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={requestedQuantity <= 1}
                >
                  <Minus size={16} />
                </button>
                
                <div className="quantity-display">
                  {requestedQuantity}
                </div>
                
                <button 
                  className="quantity-btn"
                  onClick={() => handleQuantityChange(1)}
                  disabled={requestedQuantity >= availableQuantity}
                >
                  <Plus size={16} />
                </button>
              </div>
              
              <div className="quantity-input">
                <input
                  type="number"
                  min="1"
                  max={availableQuantity}
                  value={requestedQuantity}
                  onChange={handleQuantityInput}
                  placeholder="Enter quantity"
                />
                <div className="input-note">
                  Enter quantity directly or use +/- buttons
                </div>
              </div>
            </QuantitySection>
            
            <PriceBreakdown>
              <div className="breakdown-row">
                <span>Unit Price:</span>
                <span>${unitPrice.toFixed(2)}</span>
              </div>
              <div className="breakdown-row">
                <span>Quantity:</span>
                <span>{requestedQuantity}</span>
              </div>
              <div className="breakdown-row total">
                <span>Total:</span>
                <span>${totalPrice.toFixed(2)}</span>
              </div>
            </PriceBreakdown>
            
            {error && (
              <div style={{
                color: '#ff4444',
                padding: '0.5rem',
                marginBottom: '1rem',
                background: 'rgba(255, 68, 68, 0.1)',
                borderRadius: '4px'
              }}>
                {error}
              </div>
            )}

            {/* Structured Attributes (non-restaurant items) */}
            {!isRestaurant && (item.attributes || []).filter(a => a.values?.length > 0).map(attr => (
              <div key={attr.name} style={{ marginBottom: '0.85rem' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.35rem', opacity: 0.85 }}>
                  {attr.name}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {attr.values.map(val => {
                    const selected = selectedAttributes[attr.name] === val;
                    const accent = item.theme?.colors?.accent || '#800000';
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setSelectedAttributes(prev => ({
                          ...prev,
                          [attr.name]: prev[attr.name] === val ? '' : val
                        }))}
                        style={{
                          padding: '0.35rem 0.85rem',
                          borderRadius: '20px',
                          fontSize: '0.82rem',
                          cursor: 'pointer',
                          border: selected ? `2px solid ${accent}` : '1px solid rgba(255,255,255,0.2)',
                          background: selected ? `${accent}33` : 'rgba(255,255,255,0.05)',
                          color: selected ? '#fff' : 'rgba(255,255,255,0.7)',
                          fontWeight: selected ? '700' : '400',
                          transition: 'all 0.15s',
                        }}
                      >
                        {val}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Restaurant Customization */}
            {isRestaurant && (
              <RestaurantSection theme={item.theme}>
                <RestaurantToggle
                  theme={item.theme}
                  $open={restaurantOpen}
                  onClick={() => setRestaurantOpen(o => !o)}
                  type="button"
                >
                  <span>🍽 Customize Order</span>
                  <ChevronDown size={16} />
                </RestaurantToggle>
                <RestaurantPanel $open={restaurantOpen}>

                  {/* Radio Options */}
                  {validOptions.length > 0 && (
                    <>
                      <SectionLabel theme={item.theme}>Choice</SectionLabel>
                      {validOptions.map(opt => (
                        <OptionRow key={opt} theme={item.theme}>
                          <input
                            type="radio"
                            name="restaurant-option"
                            value={opt}
                            checked={selectedOption === opt}
                            onChange={() => setSelectedOption(opt)}
                          />
                          {opt}
                        </OptionRow>
                      ))}
                    </>
                  )}

                  {/* Side Dishes by Category */}
                  {Object.entries(sidesByCategory).length > 0 && (
                    <>
                      <SectionLabel theme={item.theme}>Add Sides</SectionLabel>
                      {Object.entries(sidesByCategory).map(([cat, items]) => (
                        <div key={cat}>
                          <div style={{ fontSize: '0.75rem', opacity: 0.5, marginBottom: '0.2rem', paddingLeft: '0.4rem' }}>{cat}</div>
                          {items.map(si => (
                            <SideItem key={si.id} theme={item.theme}>
                              <input
                                type="checkbox"
                                checked={selectedSides.has(si.id)}
                                onChange={() => setSelectedSides(prev => {
                                  const next = new Set(prev);
                                  next.has(si.id) ? next.delete(si.id) : next.add(si.id);
                                  return next;
                                })}
                              />
                              {si.name}
                              {si.price && <span className="side-price">+${parseFloat(si.price).toFixed(2)}</span>}
                            </SideItem>
                          ))}
                        </div>
                      ))}
                    </>
                  )}

                  {/* Special Instructions */}
                  <SectionLabel theme={item.theme}>Special Instructions</SectionLabel>
                  <InstructionsField
                    theme={item.theme}
                    placeholder="Allergies, preferences, notes... (max 150 chars)"
                    maxLength={150}
                    value={specialInstructions}
                    onChange={e => setSpecialInstructions(e.target.value)}
                  />
                  <div style={{ fontSize: '0.75rem', opacity: 0.5, textAlign: 'right', marginTop: '0.2rem' }}>
                    {specialInstructions.length}/150
                  </div>
                </RestaurantPanel>
              </RestaurantSection>
            )}

          </>
        )}
        
        {availableQuantity <= 0 && (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            background: 'rgba(244, 67, 54, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(244, 67, 54, 0.3)'
          }}>
            <h3 style={{ color: '#F44336', marginBottom: '0.5rem' }}>Out of Stock</h3>
            <p style={{ opacity: 0.8 }}>This item is currently unavailable.</p>
          </div>
        )}

        {availableQuantity > 0 && (
          <ActionButtons>
            <button 
              className="add-to-cart"
              onClick={handleAddToCart}
              disabled={loading}
            >
              <ShoppingCart size={16} />
              {loading ? 'Adding...' : 'Add to Cart'}
            </button>
            
            <button 
              className="order-now"
              onClick={handleOrderNow}
              disabled={loading}
            >
              <Send size={16} />
              Order Now
            </button>
          </ActionButtons>
        )}
        
        {error && (
          <div style={{ 
            color: '#ff4444', 
            padding: '0.5rem', 
            marginTop: '1rem',
            background: 'rgba(255, 68, 68, 0.1)',
            borderRadius: '4px'
          }}>
            {error}
          </div>
        )}
      </DialogContent>
    </DialogOverlay>
  );
};

export default BuyDialog;